import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';
import React, { FC, useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import useRouter from 'src/presentation/hooks/useRouter';
import { TabConfig } from './TableList';

type TabsProps = {
  dataAid: string;
  onClickTab: Function;
  selectedTabLabel?: string;
  tabConfig: TabConfig[];
  tabCounts: number[];
};

const Tabs: FC<TabsProps> = ({ dataAid, onClickTab, selectedTabLabel, tabConfig, tabCounts }) => (
  <StyledTabs>
    {map(tabConfig, (config, index) => (
      <Tab
        isSelected={config.label === selectedTabLabel}
        isDisabled={!config.showContentIfNoItems && tabCounts[index] === 0}
        key={config.label}
        data-aid={`${dataAid}-${config.key}`}
        onClick={() => onClickTab(config, index === 0)}
      >
        {config.label}
      </Tab>
    ))}
  </StyledTabs>
);

const StyledTabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${p => p.theme.colors.gray300};
`;

type TabProps = {
  isSelected: boolean;
  isDisabled: boolean;
  onClick: any;
  'data-aid'?: string;
};

const Tab: FC<TabProps> = ({ children, onClick, isSelected, isDisabled, 'data-aid': dataAid }) => {
  return (
    <StyledTab
      role="tab"
      aria-disabled={isDisabled}
      aria-selected={isSelected}
      data-aid={dataAid}
      isSelected={isSelected}
      isDisabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
    >
      {children}
    </StyledTab>
  );
};

const activeTab = css`
  border-color: ${p => p.theme.colors.green400};
  color: ${p => p.theme.colors.green400};
  font-weight: bold;
`;

const disabledTab = css`
  border-bottom: 2px solid transparent;
  color: ${p => p.theme.colors.gray400};
  cursor: default;
`;

const StyledTab = styled.div`
  border-bottom: 2px solid transparent;
  padding: 8px;
  color: ${p => p.theme.colors.gray600};
  cursor: pointer;
  text-align: center;
  min-width: 120px;
  ${p => p.isSelected && activeTab};
  ${p => p.isDisabled && disabledTab};
`;

export function useTabFilter({ tabConfig, items, onTabChange, preselectedTab, updateUrlOnTabChange, root }) {
  let tabConfigLabel;
  const { history, location } = useRouter();

  const [currentTab, setCurrentTab] = useState(tabConfig?.length ? tabConfig[0] : null);
  const [priorPreselectedTab, setPriorPreselectedTab] = useState<any>(null);

  if (tabConfig) {
    const currentTabConfig = preselectedTab ? find(tabConfig, tc => tc.key === preselectedTab) : tabConfig[0];
    tabConfigLabel = get(currentTabConfig, 'label', null);
  }

  const [selectedTabLabel, changeTab] = useState(tabConfigLabel);

  const onClickTab = useCallback(
    (tabConfig, isFirstTab) => {
      if (currentTab.key === tabConfig.key) {
        return;
      }

      if (updateUrlOnTabChange) {
        if (location.pathname === root) {
          history.replace({
            ...location,
            hash: isFirstTab ? '' : `#${tabConfig.key}`,
            search: '',
          });
        }
      }

      if (selectedTabLabel !== tabConfig.label) {
        changeTab(tabConfig.label);
        onTabChange && onTabChange(tabConfig);
        setCurrentTab(tabConfig);
      }
    },
    [changeTab, currentTab, history, location, onTabChange, root, selectedTabLabel, updateUrlOnTabChange, setCurrentTab]
  );

  useEffect(() => {
    if (preselectedTab && priorPreselectedTab !== preselectedTab && currentTab.key != preselectedTab) {
      setPriorPreselectedTab(preselectedTab);

      const tab = find(tabConfig, tc => tc.key === preselectedTab);
      setCurrentTab(tab);
      onClickTab(tab, tabConfig.indexOf(tab) === 0);
    }
  }, [currentTab, onClickTab, preselectedTab, priorPreselectedTab, setCurrentTab, setPriorPreselectedTab, tabConfig]);

  const tabCounts = map(tabConfig, config => filter(get(items, config.itemsKey, items), config.filter).length);
  return [{ selectedTabLabel, onClickTab, tabConfig, tabCounts }];
}

export default Tabs;
